import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone';

// Helpers
import {
  convertTimeTo24Hour,
  convert24HourTimeToSeconds,
  convertUnixAndSecondsToDateTime,
} from 'Shared/utils/DateTime.utils';
import { isEmptyString } from 'Shared/helpers';

// Constants
import { weekDaysOptions } from 'Shared/constants/constants';

// Images
import GoogleCalendarSvg from 'Shared/images/google-calendar.svg';
import Outlook365Svg from 'Shared/images/outlook365.svg';

// Selectors
import * as OnboardingStateSelectors from 'Shared/selectors/OnboardingState.selectors';

// Actions
import { advanceOnboardingStateAction, backOnboardingStateAction } from 'Shared/actions/onboardingState';

// Components
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { WpDateInput } from 'Wpds/components/date-input';
import WpSelectV2 from 'Wpds/components/select/WpSelectV2';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpTimeInput from 'Wpds/components/time-input/WpTimeInput';
import WpCheckbox from 'Wpds/components/checkbox/WpCheckbox';
import WpLabel from 'Wpds/components/label/WpLabel';
import { WpDropdown, WpDropdownMenu, WpDropdownMenuItem, WpDropdownToggle } from 'Wpds/components/dropdown';

// Styles
import './NextMeeting.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

const NextMeeting = () => {
  const dispatch = useDispatch();

  const timezoneOptions = useSelector(state => state.default_options.time_zone_options);
  const recurringMeetings = useSelector(state => OnboardingStateSelectors.getRecurringMeetings(state));
  const connectedCalendar = useSelector(state => OnboardingStateSelectors.getConnectedCalendar(state));
  const firstWorkspace = useSelector(state => OnboardingStateSelectors.getWorkspaces(state))[0];
  const createdWorkspaces = useSelector(state => OnboardingStateSelectors.getWorkspaces(state));
  const hasRecurringMeetings = recurringMeetings.length > 0;
  const [isShowingManualCreate, setIsShowingManualCreate] = useState(false);
  const [isResettingFrom, setIsResettingForm] = useState(false);
  const [showRecurringSelect, setShowRecurringSelect] = useState(true);
  const [selectedRecurringMeetings, setSelectedRecurringMeetings] = useState([null]);
  const [teammateFullName, setTeammateFullName] = useState('');
  const [defaultDateInput, setDefaultDateInput] = useState(null);
  const [defaultTimeInput, setDefaultTimeInput] = useState(null);
  const [checkedRecurrence, setCheckedRecurrence] = useState(true);
  const [weeklyRecurrenceInput, setWeeklyRecurrenceInput] = useState('1');

  const {
    app: {
      routes: { googleCalendarSignupUrl, office365CalendarSignupUrl },
    },
  } = useApp();

  useEffect(() => {
    if (connectedCalendar && !hasRecurringMeetings) {
      setIsShowingManualCreate(true);
    }
  }, []);

  const baseClassName = 'NextMeeting';

  const maxDropDownOptions = recurringMeetings.length;

  const addRecurringMeetingDropdown = () => {
    setSelectedRecurringMeetings([...selectedRecurringMeetings, null]);
  };

  const removeRecurringMeetingDropdown = index => {
    if (selectedRecurringMeetings.length > 1) {
      const newSelections = [
        ...selectedRecurringMeetings.slice(0, index),
        ...selectedRecurringMeetings.slice(index + 1),
      ];
      setSelectedRecurringMeetings(newSelections);
    } else {
      setSelectedRecurringMeetings([null]);
    }
  };

  const handleOAuthRedirect = url => {
    const element = document.createElement('a');
    element.href = url;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const calendarEventName = 'Connected calendar (onboarding)';
  const stepName = 'NextMeeting';

  const handleSignUpWithGoogle = () => {
    (window as any).analytics.track(calendarEventName, {
      calendar_type: 'google',
      onboarding_step: stepName,
    });
    handleOAuthRedirect(googleCalendarSignupUrl);
  };

  const handleSignUpWithOffice365 = () => {
    (window as any).analytics.track(calendarEventName, {
      calendar_type: 'office365',
      onboarding_step: stepName,
    });
    handleOAuthRedirect(office365CalendarSignupUrl);
  };

  const filteredSelectedMeetings = selectedRecurringMeetings.filter(selection => !!selection);

  const disableNextButton = !(isShowingManualCreate || filteredSelectedMeetings.length > 0);

  const handleSubmit = values => {
    let newWorkspaces = [];
    if (!isShowingManualCreate) {
      newWorkspaces = filteredSelectedMeetings.map(recurringMeeting => {
        return {
          fullName: recurringMeeting.teammateName,
          recurring_meeting_uuid: recurringMeeting.uuid,
          uuid: uuidv4(),
        };
      });
    } else {
      let teammateWorkspace = {
        uuid: uuidv4(),
        fullName: values.teammate_name,
      };
      if (values.manual_meeting_date && values.manual_time_input) {
        const meetingDate = new Date(values.manual_meeting_date * 1000);
        const day = weekDaysOptions.find(({ dayNumber }) => dayNumber === meetingDate.getDay()).value;
        const timeOfDay = convertTimeTo24Hour(values.manual_time_input);
        let manualRecurrence = {};
        const dayTime = `${day} ${timeOfDay}`;
        if (values.reocurr_cb) {
          manualRecurrence = {
            at: dayTime,
            interval_amount: values.recurr_frequency_input,
            interval_unit: 'week',
            starts_at: values.manual_meeting_date,
            tz: timezoneOptions.find(option => option.value === moment.tz.guess())?.value,
          };
        }
        const meeting_time = convertUnixAndSecondsToDateTime(
          values.manual_meeting_date,
          convert24HourTimeToSeconds(timeOfDay)
        );
        const meetingDetails = { recurrence: manualRecurrence, meeting_time };
        teammateWorkspace = { ...teammateWorkspace, ...meetingDetails };
      }
      newWorkspaces = [teammateWorkspace];
    }
    dispatch(
      advanceOnboardingStateAction({
        onboardingState: {
          workspaces: newWorkspaces,
        },
        didSkip: false,
      })
    );
  };

  const handleBackClick = () => {
    dispatch(backOnboardingStateAction());
  };

  const handleSetRecurringMeeting = useCallback(
    (selected, i) => {
      selectedRecurringMeetings[i] = selected;
      setSelectedRecurringMeetings(selectedRecurringMeetings);

      // hide the manual meeting panel in case the user selected to manually create
      setIsShowingManualCreate(false);

      // reset the form to clear any pre-selected values
      setIsResettingForm(true);
      setTimeout(() => {
        setIsResettingForm(false);
      }, 0);

      // If user has made a selection in the last visible drop-down, add another drop-down field
      if (i == selectedRecurringMeetings.length - 1 && i < maxDropDownOptions - 1) {
        addRecurringMeetingDropdown();
      }
    },
    [selectedRecurringMeetings]
  );

  const getFormattedRecurringMeeting = meeting => {
    return {
      ...meeting,
      label: meeting.name,
      value: meeting.uuid,
      frequency: meeting.frequency,
      source: meeting.source,
      disabled: !!selectedRecurringMeetings.find(meetingOption => meetingOption && meetingOption.uuid == meeting.uuid),
    };
  };

  const getFormattedRecurringMeetings = () => {
    return recurringMeetings.map(meeting => getFormattedRecurringMeeting(meeting));
  };

  const customRecurringSelectOption = ({ option }) => {
    const isGoogleSource = option.source == 'google';
    return (
      <div className={`${baseClassName}__select-options`}>
        <div className={`${baseClassName}__select-options__desc`}>
          <span className={`${baseClassName}__select-options__label`}>{option.label}</span>
          <div className={`${baseClassName}__select-options__frequency`}>{option.frequency}</div>
        </div>
        <img
          src={isGoogleSource ? GoogleCalendarSvg : Outlook365Svg}
          className={`${baseClassName}__select-options__image`}
        />
      </div>
    );
  };

  const handleCreateManuallyClick = () => {
    // hide the recurring select
    setShowRecurringSelect(false);

    // Reset the selected reocurring meeting to null, incase the user selected one
    setSelectedRecurringMeetings([null]);

    // show the manual meeting input
    setIsShowingManualCreate(true);

    setTeammateFullName('');

    // reset the form to clear any pre-selected values
    setIsResettingForm(true);
    setTimeout(() => {
      setIsResettingForm(false);
    }, 0);
  };

  const handleSelectCalendarClick = () => {
    // hide the recurring select
    setShowRecurringSelect(true);

    // Reset the selected reocurring meeting to null, incase the user selected one
    setSelectedRecurringMeetings([null]);

    // show the manual meeting input
    setIsShowingManualCreate(false);

    // reset the form to clear any pre-selected values
    setIsResettingForm(true);
    setTimeout(() => {
      setIsResettingForm(false);
    }, 0);
  };

  useEffect(() => {
    if (createdWorkspaces.length) {
      if (createdWorkspaces[0].recurring_meeting_uuid) {
        handleSelectCalendarClick();
        const selectedMeetings = createdWorkspaces.map(workspace => {
          return recurringMeetings.find(meeting => meeting.uuid == workspace.recurring_meeting_uuid);
        });
        setSelectedRecurringMeetings(selectedMeetings);
      } else {
        handleCreateManuallyClick();
        setTeammateFullName(firstWorkspace.fullName);
        if (createdWorkspaces[0].meeting_time) {
          const selectedDateTime = new Date(createdWorkspaces[0].meeting_time * 1000);
          setDefaultDateInput(createdWorkspaces[0].meeting_time);
          const timeString = selectedDateTime.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          });
          setDefaultTimeInput(timeString.toLowerCase());
          const didCheckRecurrence = !!createdWorkspaces[0].recurrence.interval_amount;
          setCheckedRecurrence(didCheckRecurrence);
          if (didCheckRecurrence) {
            setWeeklyRecurrenceInput(createdWorkspaces[0].recurrence.interval_amount);
          }
        }
      }
    }
  }, []);

  return (
    <div className={baseClassName}>
      {hasRecurringMeetings && showRecurringSelect && connectedCalendar && (
        <>
          <WpLabel id="recurring_meeting_select_label"> Select your next meeting </WpLabel>
          <WpFormLayout>
            {selectedRecurringMeetings.map((meeting, i) => {
              const showAddDropDownButton =
                selectedRecurringMeetings.length < maxDropDownOptions && i == selectedRecurringMeetings.length - 1;
              return (
                <div className={`${baseClassName}__recurring-meeting-select`} key={`next_meeting_${i}`}>
                  <div style={{ maxWidth: '400px' }}>
                    <WpSelectV2
                      id="recurring_meeting_select"
                      label=""
                      prefixIcon="calendar-alt"
                      onChange={selection => handleSetRecurringMeeting(selection, i)}
                      options={getFormattedRecurringMeetings()}
                      value={meeting ? meeting.uuid : ''}
                      components={{ option: customRecurringSelectOption }}
                      isSearchable
                      searchPlaceholder="Search"
                      forceReset
                    />
                  </div>
                  <div className={`${baseClassName}__recurring-meeting-select__add-delete-buttons`}>
                    {showAddDropDownButton ? (
                      <span className={`${baseClassName}__recurring-meeting-select__add-delete-buttons__add-button`}>
                        <WpButton variant="secondary" onClick={addRecurringMeetingDropdown}>
                          <WpIcon name="plus-circle" /> add another
                        </WpButton>
                      </span>
                    ) : (
                      <WpButton variant="tertiary" onClick={() => removeRecurringMeetingDropdown(i)}>
                        <WpIcon name="times" />
                      </WpButton>
                    )}
                  </div>
                </div>
              );
            })}
            <WpButton variant="tertiary" onClick={handleCreateManuallyClick} qaTest="or-create-manually">
              or create manually
            </WpButton>
          </WpFormLayout>
        </>
      )}
      {!connectedCalendar && (
        <WpFormLayout>
          <WpDropdown>
            <WpDropdownToggle>
              <WpButton variant={'secondary'}>
                <WpIcon name="calendar-plus" />
                Import from your calendar
                <WpIcon name="chevron-down" />
              </WpButton>
            </WpDropdownToggle>
            <WpDropdownMenu>
              <WpDropdownMenuItem onClick={handleSignUpWithGoogle}>
                <img src={GoogleCalendarSvg} className={`${baseClassName}__integration-options__image`} />
                Google Calendar
              </WpDropdownMenuItem>
              <WpDropdownMenuItem onClick={handleSignUpWithOffice365}>
                <img src={Outlook365Svg} className={`${baseClassName}__integration-options__image`} />
                Office365 Calendar
              </WpDropdownMenuItem>
            </WpDropdownMenu>
          </WpDropdown>

          {!isShowingManualCreate && (
            <WpButton variant="tertiary" onClick={handleCreateManuallyClick} qaTest="or-create-manually">
              or create manually
            </WpButton>
          )}
        </WpFormLayout>
      )}
      {hasRecurringMeetings && !showRecurringSelect && (
        <WpFormLayout>
          <WpButton variant="tertiary" onClick={handleSelectCalendarClick}>
            <WpIcon name="calendar-alt" />
            select from your calendar
          </WpButton>
        </WpFormLayout>
      )}
      {!isResettingFrom && (
        <WpForm onSubmit={handleSubmit}>
          {isShowingManualCreate && (
            <WpFormLayout direction="vertical">
              <WpFormLayout direction="horizontal">
                <div style={{ maxWidth: '400px' }}>
                  <WpTextField
                    id="teammate_name"
                    label="Their full name"
                    placeholder="Aliya Predovic"
                    required
                    value={teammateFullName}
                    validate={{
                      isValidName: {
                        callback: value => !isEmptyString(value),
                        error: 'Please enter a valid name',
                      },
                    }}
                  />
                </div>
              </WpFormLayout>
              <div style={{ maxWidth: '400px' }}>
                <WpFormLayout direction="horizontal">
                  <WpDateInput
                    id="manual_meeting_date"
                    label="Next Meeting"
                    placement="right-start"
                    value={defaultDateInput}
                  />
                  <WpTimeInput id="manual_time_input" label="Time" value={defaultTimeInput} />
                </WpFormLayout>
              </div>
              <div className="d-flex align-items-center flex-wrap">
                <WpCheckbox id="reocurr_cb" label="Repeat every" checked={checkedRecurrence} />
                <span style={{ maxWidth: '75px', margin: '0 .5rem' }}>
                  <WpTextField
                    id="recurr_frequency_input"
                    label="how often"
                    labelHidden
                    type="number"
                    value={weeklyRecurrenceInput}
                    min={1}
                  />
                </span>
                <span>week(s)</span>
              </div>
            </WpFormLayout>
          )}

          <div className={`${baseClassName}__footer`}>
            <WpButtonGroup>
              <WpButton btnType="submit" qaTest="next-meeting-next" disabled={disableNextButton}>
                Next
              </WpButton>
              <WpButton color="gray" variant="tertiary" onClick={handleBackClick}>
                Back
              </WpButton>
            </WpButtonGroup>
          </div>
        </WpForm>
      )}
    </div>
  );
};

export default NextMeeting;
